import { ComponentProps, ReactNode, useMemo, useRef, useState } from 'react';
import { Icon } from 'vopay';
import AlertDialog from '#shared/components/AlertDialog/AlertDialog';
import {
  AlertDialogContext,
  AlertDialogMethod,
  AlertDialogProps,
  AlertDialogResult,
  AlertDialogService,
} from './AlertDialogContext';

type AlertDialogProviderProps = {
  children: ReactNode;
  // Marks a host's stopgap provider, mounted above its own dialog/modal providers so a component rendered
  // outside <SharedProviders> gets a working alert instead of useAlertDialog() throwing. Every alert it
  // shows logs an error, so the offending call sites can be found and moved under SharedProviders.
  fallback?: boolean;
};

export default function AlertDialogProvider({ children, fallback = false }: AlertDialogProviderProps) {
  const [props, setProps] = useState<AlertDialogProps>({ isOpen: false, onClose: () => settle('close') });
  // Resolver of the alert currently showing. Every way the alert can go away — a button, Escape,
  // `close()`, or another alert replacing it — settles this first, so no caller is left awaiting a
  // promise whose dialog is gone.
  const pendingResolveRef = useRef<((result: AlertDialogResult) => void) | null>(null);

  function settle(result: AlertDialogResult) {
    pendingResolveRef.current?.(result);
    pendingResolveRef.current = null;
    setProps((prev) => ({ ...prev, isOpen: false }));
  }

  function createMethod(icon?: ComponentProps<typeof Icon>['icon']): AlertDialogMethod {
    return (title, content, options) =>
      new Promise<AlertDialogResult>((resolve) => {
        if (fallback) {
          console.error(
            'useAlertDialog: this alert came from the fallback AlertDialogProvider, so the calling component renders ' +
              "outside <SharedProviders>. Open it through shared's useDialog() instead.",
          );
        }

        pendingResolveRef.current?.('close');
        pendingResolveRef.current = resolve;

        setProps({
          isOpen: true,
          title,
          content,
          top: icon && <Icon icon={icon} size="jumbo" width="104px" height="104px" color="none" />,
          onClose: () => settle('close'),
          onAbort: () => settle('abort'),
          onConfirm: () => settle('confirm'),
          ...options,
        });
      });
  }

  // Built once: the methods only close over setProps and the ref, both stable, so consumers get one
  // service identity for the provider's lifetime and can put it in dependency arrays without
  // re-firing on every open/close.
  const service = useMemo<AlertDialogService>(
    () => ({
      open: createMethod(),
      success: createMethod('dialogSuccess'),
      caution: createMethod('cautionary2'),
      error: createMethod('errorRed2'),
      close: () => settle('close'),
    }),
    [fallback],
  );

  return (
    <AlertDialogContext.Provider value={service}>
      {children}
      <AlertDialog {...props} />
    </AlertDialogContext.Provider>
  );
}
